import { zodResolver } from '@hookform/resolvers/zod'
import { Box } from '@mui/material'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../../app/hooks'
import { useNotificationStack } from '../../app/hooks/useNotificationStack'
import { useCreateAssetMutation, useUpdateAssetMutation } from '../../app/redux-fetch/apiAssets'
import CustomAlert from '../../components/Alerts/CustomAlert'
import SubmitButtons from '../../components/Buttons/SubmitButtons'
import TabWrapper, { TabItem } from '../../components/TabWrapper/TabWrapper'
import Documents from '../Documents/Documents'
import { fetchLoggedInUserSelector } from '../selectors'
import { IAssetManagement, ILoggedInUser } from '../types'
import { DetailsTab } from './DetailsTab'
import { messages } from './messages'
import { useSideModalContext } from './modals/SideModalContext'
import PhysicalProperties from './PhysicalProperties'
import { AssetManagementSchemaType, getAssetManagementValidationSchema } from './schemas/assetManagementSchema'

const AssetForm = ({ asset, isDisabled = false }: { asset: IAssetManagement; isDisabled?: boolean }) => {
  const { onClose } = useSideModalContext()
  const { formatMessage } = useIntl()
  const { tenantId } = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
  const isNew = !asset.id
  const { enqueueSuccess, enqueueFailure } = useNotificationStack()
  const [createAsset] = useCreateAssetMutation()
  const [updateAsset] = useUpdateAssetMutation()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validationSchema = getAssetManagementValidationSchema(formatMessage)

  const methods = useForm<AssetManagementSchemaType>({
    defaultValues: asset as AssetManagementSchemaType,
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  })

  const {
    handleSubmit,
    formState: { isDirty, errors },
    reset,
    watch,
  } = methods

  const isUsedForExport = watch('detail.isUsedForExport')

  const fixProductionYear = (detail: any) => {
    if (!detail) return detail
    const py = detail.productionYear
    if (py === null) {
      detail.productionYear = undefined
    }
    return detail
  }

  const onFormSubmit = async (values: AssetManagementSchemaType) => {
    try {
      if (!isDirty) {
        onClose()
        return
      }
      setIsSubmitting(true)
      const fixedValues = {
        ...values,
        detail: fixProductionYear(values.detail),
        tenantId,
        documents: values.documents ?? [],
      }
      if (isNew) {
        await createAsset({
          tenantId,
          body: fixedValues,
        }).unwrap()
        enqueueSuccess(messages.assetCreated)
      } else {
        await updateAsset({
          tenantId,
          body: fixedValues,
        }).unwrap()
        enqueueSuccess(messages.assetUpdated)
      }
      onClose()
      reset(values)
    } catch (error) {
      enqueueFailure(isNew ? messages.assetNotCreated : messages.assetNotUpdated)
    } finally {
      setIsSubmitting(false)
    }
  }

  const tabs: TabItem[] = [
    {
      label: formatMessage(messages.assetsDetails),
      content: <DetailsTab isDisabled={isDisabled} tenantId={tenantId} assetId={asset.id} />,
      hasError: Boolean(
        errors.assetClassId || errors.assetTypeId || errors.transportModeId || errors.assetCategoryId || errors.identification || errors.detail
      ),
    },
    {
      label: formatMessage(messages.physicalProperties),
      content: <PhysicalProperties isDisabled={isDisabled} />,
      hasError: Boolean(errors.physicalProperties),
    },

    {
      label: formatMessage(messages.documents),
      content: <Box sx={{ width: '100%', m: '16px' }}>
        <Documents
          categoryId={2}
          entityId={asset?.id}
          isDisabled={isDisabled}
          tenantId={tenantId}
        /></Box>,
      hasError: Boolean(errors.detail?.isUsedForExport || errors.detail?.exportDocumentationAvailable || errors.detail?.exportDocumentationExpiry || errors.documents),
    },
  ]

  const hasExportValidationErrors = Boolean(
    errors.detail?.isUsedForExport ||
    errors.detail?.exportDocumentationAvailable ||
    errors.detail?.exportDocumentationExpiry ||
    errors.documents
  )

  const getExportErrorMessage = () => {
    if (errors.documents) {
      return formatMessage(messages.documentsRequiredForExport)
    }
    if (errors.detail?.exportDocumentationAvailable) {
      return formatMessage(messages.exportDocumentationAvailableRequired)
    }
    if (errors.detail?.exportDocumentationExpiry) {
      return formatMessage(messages.exportDocumentationExpiryRequired)
    }
    return formatMessage(messages.exportDocumentationMissing)
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Box sx={{ mt: '64px' }}>
          {hasExportValidationErrors && (
            <CustomAlert
              severity='error'
              title='Export Documentation Required'
              message={getExportErrorMessage()}
            />
          )}
          <TabWrapper tabs={tabs} variant='standard' />
        </Box>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            padding: '16px',
            backgroundColor: '#fff',
            borderTop: '1px solid #e0e0e0',
            zIndex: 999,
          }}
        >
          {!isDisabled && (
            <SubmitButtons
              create={isNew}
              isEnabled={!isSubmitting}
              showEnabledSwitch={true}
              submitForm={handleSubmit(onFormSubmit)}
              onCancel={onClose}
            />
          )}
        </Box>
      </form>
    </FormProvider>
  )
}

export default AssetForm
